import { useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import State from "../../../../../../../../context";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Breadcrumb, Flex, Skeleton, Tabs, Button } from "antd";
import ModalCompanyUserArchive from "../ModalCompanyUserArchive";
import ModalCompanyUserSettings from "../../../../component/ModalCompanyUserSettings";
import ModalEnableInvestments from "../../../../component/ModalEnableInvestments";
import ModalLevelProductDetails from "../../../../../admin/component/ModalLevelProductDetails";
import ModalSettingsLogo from "./component/ModalSettingsLogo";
import ModalSettingsTheme from "./component/ModalSettingsTheme";
import ModalSettingsVideo from "./component/ModalSettingsVideo";
import PageProductsPrimary from "../../../PageProductsPrimary";
import SectionCompanySettings from "./component/SectionCompanySettings";
import TabConnections from "./component/TabConnections";
import TabUsers from "./component/TabUsers";
import TemplatePageDashboard from "../../../../../admin/page/PageDashboard/template";

import StyledContainer from "./style";

import {
  addCompanyUser,
  archiveCompanyUser,
  getCompanyById,
  getCompanyUsers,
  getUserTypes,
  updateEnabledInvestmentsForAdmin,
  updateCompanyUserBySuperadmin,
  getCompanyEnterpriseAdmins,
  generateJwtBySuperadmin,
  updateCompanySettingsByID,
  uploadCompanyLogoByID,
} from "../../../../request";
import {
  isPermissionReadonly,
  isTypeAffiliateCompany,
  isTypeProPortalAffiliateCompany,
  isTypeProPortalCompany,
} from "../../../../helper";

import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { faPenToSquare } from "@fortawesome/free-regular-svg-icons";
import { ReactComponent as DotSeparator } from "../../../../image/dot_separator.svg";

const SectionCompanyOne = () => {
  const [state] = useContext(State);
  const [companyData, setCompanyData] = useState();

  useEffect(() => {
    state.setKeyValue("loading", true);

    getCompanyById({ companyId: state.activeCompanyId, userEmail: state._id })
      .then(responseCompanyData => {
        getCompanyEnterpriseAdmins({
          companyId: state.activeCompanyId,
          userEmail: state._id,
        })
          .then(responseEnterpriseAdmins => {
            setCompanyData({
              ...responseCompanyData,
              managedUserManagers: responseEnterpriseAdmins
                .map(it => it.managedUserManagers)
                .filter(it => !!it)
                .flat(),
            });

            getCompanyUsers({
              companyId: state.activeCompanyId,
              userEmail: state._id,
            })
              .then(data => state.setKeyValue("activeCompanyUsersList", data))
              .catch(state.showError)
              .finally(() => state.setKeyValue("loading", false));
          })
          .catch(error => {
            state.showError(error);
            state.setKeyValue("loading", false);
          });
      })
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });

    if (!state.userTypes) {
      getUserTypes()
        .then(data => state.setKeyValue("userTypes", data))
        .catch(state.showError);
    }
  }, []);

  const handleCompanyUserArchive = () => {
    state.setKeyValue("loading", true);

    archiveCompanyUser({
      companyId: state.activeCompanyId,
      companyUserId: state.activeUserId,
      userEmail: state._id,
    })
      .then(() =>
        getCompanyUsers({
          companyId: state.activeCompanyId,
          userEmail: state._id,
        })
          .then(data => {
            state.setKeyValue("activeCompanyUsersList", data);
            state.showSuccess("User archived!");
            state.setKeyValue("openModalCompanyUserArchive", false);
          })
          .catch(state.showError)
          .finally(() => state.setKeyValue("loading", false))
      )
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const onClickProductDetails = productDetailsId => {
    state.setKeyValue("productDetailsId", productDetailsId);
    state.setKeyValue("openModalProductDetails", true);
  };

  const onClickUpdateLogo = () => {
    state.setKeyValue("openModalLogo", true);
  };

  const onClickUpdateTheme = () => {
    state.setKeyValue("openModalTheme", true);
  };

  const onClickUpdateVideo = () => {
    state.setKeyValue("openModalDashboardVideo", true);
  };

  const onClickViewAllProposals = () => {
    const primaryContactEmail = companyData?.primaryContact?.email;

    if (!primaryContactEmail) {
      state.showWarning("Missed primary contact for Company");
      return;
    }

    generateJwtBySuperadmin({
      clientEmail: primaryContactEmail,
      userEmail: state._id,
    })
      .then(response => {
        Cookies.set(`jwt__${primaryContactEmail.toLowerCase()}`, response.jwt, {
          expires: 0.5, // half a day (12 hours)
          secure: true,
        });

        window.open(
          `/wealth-admin-portal/proposals?email=${encodeURIComponent(primaryContactEmail)}`,
          isPermissionReadonly(state.permissions?.accessLevel)
            ? "windowReadonly"
            : ""
        );
      })
      .catch(state.showError);
  };

  const onClickViewProposal = proposalEmail => {
    generateJwtBySuperadmin({
      clientEmail: proposalEmail,
      userEmail: state._id,
    })
      .then(response => {
        Cookies.set(`jwt__${proposalEmail.toLowerCase()}`, response.jwt, {
          expires: 0.5, // half a day (12 hours)
          secure: true,
        });

        window.open(
          `/level-proposal?email=${encodeURIComponent(proposalEmail)}`,
          isPermissionReadonly(state.permissions?.accessLevel)
            ? "windowReadonly"
            : ""
        );
      })
      .catch(state.showError);
  };

  const submitCompanyUserSettings = values => {
    state.setKeyValue("loading", true);

    const requestPayload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone + "",
      phoneAlt: (values.phoneAlt ?? "") + "",
      type: values.type,
    };

    if (state.activeUserId) {
      updateCompanyUserBySuperadmin({
        companyId: companyData.id,
        companyUserId: state.activeUserId,
        requestPayload,
        userEmail: state._id,
      })
        .then(() =>
          getCompanyUsers({
            companyId: state.activeCompanyId,
            userEmail: state._id,
          })
            .then(data => {
              state.setKeyValue("activeCompanyUsersList", data);
              state.showSuccess("New user added.");
              state.setKeyValue("openModalCompanyUserSettings", false);
            })
            .catch(state.showError)
            .finally(() => state.setKeyValue("loading", false))
        )
        .catch(error => {
          state.showError(error);
          state.setKeyValue("loading", false);
        });
    } else {
      addCompanyUser({
        companyId: state.activeCompanyId,
        requestPayload,
        userEmail: state._id,
      })
        .then(() =>
          getCompanyUsers({
            companyId: state.activeCompanyId,
            userEmail: state._id,
          })
            .then(data => {
              state.setKeyValue("activeCompanyUsersList", data);
              state.showSuccess("New user added.");
              state.setKeyValue("openModalCompanyUserSettings", false);
            })
            .catch(state.showError)
            .finally(() => state.setKeyValue("loading", false))
        )
        .catch(error => {
          state.showError(error);
          state.setKeyValue("loading", false);
        });
    }
  };

  const submitDashboardVideo = ({ videoUrl }) => {
    submitSettings({
      video: {
        ...companyData?.settings?.video,
        url: videoUrl,
      },
    });
  };

  const submitEnableInvestments = values => {
    if (!values) return;

    state.setKeyValue("loading", true);

    updateEnabledInvestmentsForAdmin({
      companyId: state.activeCompanyId,
      requestPayload: values,
      userEmail: state._id,
    })
      .then(() =>
        getCompanyById({
          companyId: state.activeCompanyId,
          userEmail: state._id,
        })
          .then(data => {
            setCompanyData(data);
            state.showSuccess("Investments updated");
            state.setKeyValue("openModalEnableInvestments", false);
          })
          .catch(state.showError)
          .finally(() => state.setKeyValue("loading", false))
      )
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const submitLogo = fileData => {
    if (!fileData?.file?.fileList) return;

    state.setKeyValue("loading", true);

    const formData = new FormData();
    formData.append("file", fileData.file.fileList[0].originFileObj);

    uploadCompanyLogoByID({
      companyId: state.activeCompanyId,
      requestPayload: formData,
      userEmail: state._id,
    })
      .then(({ settings }) => {
        setCompanyData(oldState => ({
          ...oldState,
          settings,
        }));
        state.showSuccess("Company Logo Updated.");
        state.setKeyValue("openModalLogo", false);
      })
      .catch(state.showError)
      .finally(() => state.setKeyValue("loading", false));
  };

  const submitSettings = requestPayload => {
    state.setKeyValue("loading", true);

    updateCompanySettingsByID({
      companyId: companyData.id,
      userEmail: state._id,
      requestPayload,
    })
      .then(() => {
        getCompanyById({
          companyId: state.activeCompanyId,
          userEmail: state._id,
        })
          .then(updatedCompanyData => {
            setCompanyData(lastState => ({
              ...lastState,
              ...updatedCompanyData,
            }));
            state.showSuccess("Settings updated!");
            state.setKeyValue("openModalDashboardVideo", false);
          })
          .catch(state.showError)
          .finally(() => state.setKeyValue("loading", false));
      })
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const submitTheme = data => {
    submitSettings({
      theme: {
        ...companyData?.settings?.theme,
        data,
      },
    });
  };

  const toggleDashboardVideo = isEnabled => {
    submitSettings({
      video: {
        ...companyData?.settings?.video,
        enabled: isEnabled,
      },
    });
  };

  const toggleFeature = name => {
    const typeExistInDB = !!companyData?.settings?.features?.find(
      it => it.name === name
    );

    const updatedFeatures = typeExistInDB
      ? companyData?.settings?.features?.map(it => {
          if (it.name === name) {
            return {
              name,
              enabled: !it.enabled,
            };
          } else {
            return it;
          }
        })
      : [
          ...companyData?.settings?.features,
          {
            name,
            enabled: true,
          },
        ];

    submitSettings({
      features: updatedFeatures,
    });
  };

  const toggleTheme = isEnabled => {
    submitSettings({
      theme: {
        ...companyData?.settings?.theme,
        enabled: isEnabled,
      },
    });
  };

  const items = [
    {
      children: (
        <Skeleton
          active
          loading={state.loading}
          style={{ padding: "24px 32px" }}
        >
          <TemplatePageDashboard
            analytics={state.analytics}
            managedUserManagers={companyData?.managedUserManagers}
            onClickProductDetails={onClickProductDetails}
            onClickViewAllProposals={onClickViewAllProposals}
            onClickViewProposal={onClickViewProposal}
            productsList={state.productsList}
          />
        </Skeleton>
      ),
      hidden:
        isTypeAffiliateCompany(companyData?.type) ||
        isTypeProPortalCompany(companyData?.type) ||
        isTypeProPortalAffiliateCompany(companyData?.type),
      key: "overview",
      label: "Overview",
    },
    {
      children: <TabUsers companyData={companyData} />,
      key: "users",
      label: "Users",
    },
    {
      children: (
        <div style={{ padding: "24px 32px" }}>
          <PageProductsPrimary
            companyFilters={companyData?.products}
            hideHeader
            showEditInvestments
          />
        </div>
      ),
      hidden:
        isTypeAffiliateCompany(companyData?.type) ||
        isTypeProPortalCompany(companyData?.type) ||
        isTypeProPortalAffiliateCompany(companyData?.type),
      key: "products",
      label: "Products",
    },
    {
      children: <TabConnections />,
      hidden:
        isTypeAffiliateCompany(companyData?.type) ||
        isTypeProPortalCompany(companyData?.type) ||
        isTypeProPortalAffiliateCompany(companyData?.type),
      key: "connections",
      label: "Connections",
    },
    {
      children: (
        <SectionCompanySettings
          onClickUpdateLogo={onClickUpdateLogo}
          onClickUpdateTheme={onClickUpdateTheme}
          onClickUpdateVideo={onClickUpdateVideo}
          settings={companyData?.settings}
          toggleDashboardVideo={toggleDashboardVideo}
          toggleFeature={toggleFeature}
          toggleTheme={toggleTheme}
        />
      ),
      hidden:
        isTypeAffiliateCompany(companyData?.type) ||
        isTypeProPortalCompany(companyData?.type) ||
        isTypeProPortalAffiliateCompany(companyData?.type),
      key: "settings",
      label: "Settings",
    },
  ].filter(it => !it.hidden);

  return (
    <StyledContainer>
      <Breadcrumb
        items={[
          {
            onClick: () => state.setKeyValue("activeSection", "companyAll"),
            title: (
              <Flex align="center" gap={8} style={{ cursor: "pointer" }}>
                <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 14 }} />
                <b>All Companies</b>
              </Flex>
            ),
          },
          {
            title: <span>Company Profile</span>,
          },
        ]}
        separator={<DotSeparator style={{ color: "#C9C8C8" }} />}
      />

      <Skeleton active loading={!companyData} style={{ padding: "8px 32px" }}>
        <header>
          <Flex gap={4} vertical>
            <Flex align="center" gap={16}>
              <h2>{companyData?.name}</h2>
              <Button
                disabled={state.loading}
                icon={<FontAwesomeIcon icon={faPenToSquare} />}
                onClick={() =>
                  state.setKeyValue("openModalCompanySetting", true)
                }
                shape="round"
                size="small"
                style={{ height: 28, minHeight: 28 }}
              />
            </Flex>

            <Flex align="center" gap={8}>
              {companyData?.type}
              <DotSeparator /> Added:{" "}
              {dayjs(companyData?.createdAt).format("MM/DD/YYYY")}
            </Flex>
          </Flex>
        </header>
        <Tabs
          defaultActiveKey={state.activeTabSectionOneCompany}
          items={items}
        />
      </Skeleton>

      <ModalCompanyUserArchive
        companyUserData={state.activeCompanyUsersList?.find(
          it => it.id === state.activeUserId
        )}
        loading={state.loading}
        onCancel={() => state.setKeyValue("openModalCompanyUserArchive", false)}
        onConfirm={handleCompanyUserArchive}
        open={state.openModalCompanyUserArchive}
      />
      <ModalCompanyUserSettings
        companyName={companyData?.name}
        companyType={companyData?.type}
        loading={state.loading}
        onCancel={() =>
          state.setKeyValue("openModalCompanyUserSettings", false)
        }
        onFinish={submitCompanyUserSettings}
        open={state.openModalCompanyUserSettings}
        userData={state.activeCompanyUsersList?.find(
          it => it.id === state.activeUserId
        )}
        userTypes={state.userTypes}
      />
      <ModalEnableInvestments
        loading={state.loading}
        onCancel={() => state.setKeyValue("openModalEnableInvestments", false)}
        onSuccess={submitEnableInvestments}
        open={state.openModalEnableInvestments}
        products={companyData?.products}
        productsList={state.productsList}
      />
      <ModalLevelProductDetails
        handleClose={() => state.setKeyValue("openModalProductDetails", false)}
        investmentAssumptions={state.investmentAssumptions}
        open={state.openModalProductDetails}
        product={state.productsList?.find(
          it => it._id === state.productDetailsId
        )}
        showError={state.showError}
      />
      <ModalSettingsLogo
        activeLogoUrl={companyData?.settings?.logo?.url}
        loading={state.loading}
        onCancel={() => state.setKeyValue("openModalLogo", false)}
        onSuccess={submitLogo}
        open={state.openModalLogo}
      />
      <ModalSettingsTheme
        activeThemeId={companyData?.settings?.theme?.data?.id}
        loading={state.loading}
        onCancel={() => state.setKeyValue("openModalTheme", false)}
        onSuccess={submitTheme}
        open={state.openModalTheme}
      />
      <ModalSettingsVideo
        activeVideoUrl={companyData?.settings?.video?.url}
        loading={state.loading}
        onCancel={() => state.setKeyValue("openModalDashboardVideo", false)}
        onSuccess={submitDashboardVideo}
        open={state.openModalDashboardVideo}
      />
    </StyledContainer>
  );
};

export default SectionCompanyOne;
